import { Button, Col, Divider, Drawer, Row, Spin, Table } from 'antd';
import { useGetCaseTypeDetailsQuery } from 'services/case-type/case-type-api';
import { useGetDataSetQuery } from 'services/data-set/data-set-api';
import DataSetTypes from 'services/data-set/types';
import styled from 'styled-components';

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 12px 30px;
    position: relative;
  }
`;

const RegulationsTableWrapper = styled.div`
  .ant-table-content {
    scrollbar-color: #a2a1a1 #dbdbdb;
    scrollbar-width: thin;
  }
`;

const DrawerTitle = styled.div`
  font-family: 'RobotoCondensed';
  display: flex;
  align-items: center;
  justify-content: space-between;
  h1 {
    color: ${(props) => props.theme.colors.brandBlue};
    margin-bottom: 0;
  }
`;

const Wrapper = styled.div`
  margin-bottom: 24px;
  position: relative;
`;

const ActionButton = styled(Button)`
  float: right;
  margin-left: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  span h2 {
    color: ${(props) => props.theme.colors.brandBlue};
    font-family: 'RobotoCondensedBold';
    font-size: 1.6rem;
  }
`;

const SectionDivider = styled(Divider)`
  font-family: 'RobotoCondensed';
  font-size: 1.5rem !important;
`;

const FieldLabel = styled.div`
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 4px;
`;

const FieldValue = styled.div`
  margin-bottom: 16px;
`;

const regulationColumns = [
  { title: 'Code', dataIndex: 'code', width: 130 },
  { title: 'Name', dataIndex: 'name', width: 220, ellipsis: true },
  { title: 'Description', dataIndex: 'description', width: 320, ellipsis: true },
  { title: 'Status', dataIndex: 'status', width: 120 },
  { title: 'CelexNr', dataIndex: 'celexNumber', width: 120, render: (v?: string) => v || '-' },
  {
    title: 'EUR-Lex',
    dataIndex: 'eurLexReference',
    width: 240,
    ellipsis: true,
    render: (ref?: string) =>
      ref ? (
        <a href={ref} target="_blank" rel="noopener noreferrer" title={ref}>
          {ref}
        </a>
      ) : (
        '-'
      ),
  }
];

export interface ICaseTypeDetailsProps {
  close: () => void;
  selectedCaseType: string;
}

const CaseTypeDetails = ({
  close,
  selectedCaseType,
}: ICaseTypeDetailsProps) => {
  const rowGutter = 16;

  const { data, error, isLoading } =
    useGetCaseTypeDetailsQuery(selectedCaseType);

  const renderContent = () => {
    if (isLoading) {
      return <Spin />;
    }
    if (error) {
      return <p>{String(error)}</p>;
    }

    return (
      <Wrapper>
        {data && (
          <>
            <DrawerTitle>
              <h1>
                {data.caseTypeId}. {data.caseName}
              </h1>
            </DrawerTitle>
            <p style={{ fontWeight: 600, marginBottom: 0 }}>
              Version {data?.version}
            </p>
          </>
        )}
        <SectionDivider titlePlacement="start" styles={{ content: { margin: 0 } }}>
          Basic Info
        </SectionDivider>

        <Row gutter={rowGutter}>
          <Col span={8}>
            <FieldLabel>Name</FieldLabel>
            <FieldValue>{data?.caseName || '-'}</FieldValue>
          </Col>
          <Col span={4}>
            <FieldLabel>Status</FieldLabel>
            <FieldValue>{data?.status || '-'}</FieldValue>
          </Col>
        </Row>
        <Row gutter={rowGutter}>
          <Col span={8}>
            <FieldLabel>Azure Active Directory Security Group</FieldLabel>
            <FieldValue>{data?.securityGroupName || data?.securityGroupAzureAd || '-'}</FieldValue>
          </Col>
        </Row>

        <SectionDivider titlePlacement="start" styles={{ content: { margin: 0 } }}>
          Extended Details
        </SectionDivider>
        <Row gutter={rowGutter}>
          <Col span={16}>
            <FieldLabel>Default PCode</FieldLabel>
            <FieldValue>{data?.defaultPCode || '-'}</FieldValue>
          </Col>
        </Row>
        <Row gutter={rowGutter}>
          <Col span={8}>
            <FieldLabel>Process Type</FieldLabel>
            <FieldValue>{data?.processTypeDescription || '-'}</FieldValue>
          </Col>
          <Col span={8}>
            <FieldLabel>Food domain</FieldLabel>
            <FieldValue>{data?.foodDomainDescription || '-'}</FieldValue>
          </Col>
          <Col span={8}>
            <FieldLabel>Authorisation Type</FieldLabel>
            <FieldValue>{data?.authorisationTypeDescription || '-'}</FieldValue>
          </Col>
          <Col span={8}>
            <FieldLabel>Application Type</FieldLabel>
            <FieldValue>{data?.applicationTypeDescription || '-'}</FieldValue>
          </Col>
          <Col span={8}>
            <FieldLabel>Risk Assessment deadline</FieldLabel>
            <FieldValue>{data?.riskAssessmentDeadlineCode || '-'}</FieldValue>
          </Col>
          <Col span={8}>
            <FieldLabel>Validation deadline</FieldLabel>
            <FieldValue>{data?.validationDeadlineCode || '-'}</FieldValue>
          </Col>
        </Row>
        <Row gutter={rowGutter}>
          <Col span={8}>
            <FieldLabel>Initial Confidentiality claims processed by</FieldLabel>
            <FieldValue>{data?.initialConfidentialityClaimsProcessedByCode || '-'}</FieldValue>
          </Col>
          <Col span={8}>
            <FieldLabel>Dossier validation processed by</FieldLabel>
            <FieldValue>{data?.dossierValidationProcessedByCode || '-'}</FieldValue>
          </Col>
          <Col span={8}>
            <FieldLabel>Request for Information by</FieldLabel>
            <FieldValue>{data?.requestForInformationByCode || '-'}</FieldValue>
          </Col>
          <Col span={8}>
            <FieldLabel>Param Catalogue Required</FieldLabel>
            <FieldValue>
              {data?.paramCatalogueRequired !== undefined
                ? data?.paramCatalogueRequired
                  ? 'Yes'
                  : 'No'
                : '-'}
            </FieldValue>
          </Col>
          <Col span={8}>
            <FieldLabel>Has PreApplication</FieldLabel>
            <FieldValue>
              {data?.hasPreApplication !== undefined
                ? data?.hasPreApplication
                  ? 'Yes'
                  : 'No'
                : '-'}
            </FieldValue>
          </Col>
        </Row>

        <SectionDivider titlePlacement="start" styles={{ content: { margin: 0 } }}>
          Leading Unit
        </SectionDivider>
        {data?.unit ? (
          <Row gutter={rowGutter}>
            <Col span={8}>
              <FieldLabel>Department</FieldLabel>
              <FieldValue>{data.unit.departmentName || '-'}</FieldValue>
            </Col>
            <Col span={4}>
              <FieldLabel>Type</FieldLabel>
              <FieldValue>{data.unit.type || '-'}</FieldValue>
            </Col>
            <Col span={8}>
              <FieldLabel>Parent Department</FieldLabel>
              <FieldValue>{data.unit.parentDepartmentName || '-'}</FieldValue>
            </Col>
            <Col span={4}>
              <FieldLabel>Status</FieldLabel>
              <FieldValue>{data.unit.status || '-'}</FieldValue>
            </Col>
          </Row>
        ) : (
          <FieldValue>No leading unit assigned</FieldValue>
        )}

        <SectionDivider titlePlacement="start" styles={{ content: { margin: 0 } }}>
          Regulations
        </SectionDivider>
        <RegulationsTableWrapper>
          <Table
          size="small"
          rowKey="id"
          dataSource={data?.regulations ?? []}
          columns={regulationColumns}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No regulations assigned' }}
        />
        </RegulationsTableWrapper>
      </Wrapper>
    );
  };

  return (
    <StyledDrawer
      open
      title={
        <Header>
          <span>
            <h2 style={{ marginBottom: 0 }}>CaseType Overview</h2>
          </span>
          <ActionButton onClick={close}>Close</ActionButton>
        </Header>
      }
      width={1200}
      onClose={close}
      styles={{ body: { paddingBottom: 80 } }}
    >
      {renderContent()}
    </StyledDrawer>
  );
};

export default CaseTypeDetails;